import { useMemo, useState } from 'react';
import { Box, useDisclosure } from '@chakra-ui/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  StageStatistics,
  BranchStageStatistics,
} from '@texas/api/endpoints/statisticsApi';
import { useTranslation } from 'react-i18next';
import { ArticlesOverviewModal } from '../shared/components/articles/ArticlesOverviewModal';
import { defaultEmptyFilters } from '@texas/api/endpoints/search/searchVariantsTypes';
import { TableColumns } from '@texas/components/searchArticles/TableColumns';
import {
  BranchIdentifier,
  getBranchColor,
} from '@texas/utils/helpers/texas/branchHelpers';
import { FilterOptionType } from '@texas/components/filter/types/types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

interface ProgressChartProps {
  data: StageStatistics[];
  earliestCreatedDate?: string;
  branchStages: BranchStageStatistics[];
}

export function ProgressChart({
  data,
  earliestCreatedDate,
  branchStages,
}: ProgressChartProps) {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [selectedData, setSelectedData] = useState<{
    articleIds: number[];
    stageName: string;
    branchId?: number;
  } | null>(null);

  const chartData = useMemo(() => {
    const labels = data.map((stage, index) => {
      if (index === 0 && earliestCreatedDate) {
        const date = new Date(earliestCreatedDate);
        return [
          stage.stageName,
          t('widgets.articleProgress.cutoff', {
            date,
            formatParams: {
              date: { year: 'numeric', month: 'short', day: 'numeric' },
            },
          }),
        ];
      }
      return stage.stageName;
    });
    const counts = data.map((stage) => stage.count);
    const percentages = data.map((stage) => stage.percentage);

    const branchDatasets = branchStages.map((branchStat) => {
      const branchIdentifier = branchStat.branchIdentifier as BranchIdentifier;
      const branchColor = getBranchColor(branchIdentifier);

      const branchData = branchStat.stages.map((stage) => stage.count);

      const cssVar = branchColor.match(/var\((--chakra-colors-[^)]+)\)/)?.[1];
      let resolvedColor = branchColor;

      if (cssVar) {
        const computedColor = getComputedStyle(document.documentElement)
          .getPropertyValue(
            cssVar.replace('--chakra-colors-', '--chakra-colors-'),
          )
          .trim();

        if (computedColor) {
          resolvedColor = computedColor;
        }
      }

      return {
        label: branchStat.branchName,
        data: branchData,
        borderColor: resolvedColor,
        backgroundColor: resolvedColor,
        tension: 0.1,
        borderWidth: 2,
      };
    });

    const averageDataset = {
      label: t('widgets.articleProgress.average'),
      data: counts,
      borderColor: 'rgb(75, 192, 192)',
      backgroundColor: 'rgba(75, 192, 192, 0.5)',
      tension: 0.1,
      borderWidth: 3,
      borderDash: [5, 5],
    };

    return {
      labels,
      datasets: [...branchDatasets, averageDataset],
      percentages,
    };
  }, [data, t, earliestCreatedDate, branchStages]);

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (_, elements) => {
        if (elements.length > 0) {
          const element = elements[0];
          const datasetIndex = element.datasetIndex;
          const dataIndex = element.index;

          if (datasetIndex < branchStages.length) {
            const branchStat = branchStages[datasetIndex];
            const stage = data[dataIndex];

            if (stage.missingArticleIds.length > 0) {
              setSelectedData({
                articleIds: stage.missingArticleIds,
                stageName: stage.stageName,
                branchId: branchStat.branchId,
              });
              onOpen();
            }
          } else {
            const stage = data[dataIndex];

            if (stage.missingArticleIds.length > 0) {
              setSelectedData({
                articleIds: stage.missingArticleIds,
                stageName: stage.stageName,
              });
              onOpen();
            }
          }
        }
      },
      onHover: (event: any, activeElements: any[]) => {
        if (event.native?.target) {
          event.native.target.style.cursor =
            activeElements.length > 0 ? 'pointer' : 'default';
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: t('widgets.articleProgress.stage'),
          },
        },
        y: {
          title: {
            display: true,
            text: t('widgets.articleProgress.articleCount'),
          },
          beginAtZero: true,
        },
      },
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: t('widgets.articleProgress.chartTitle'),
        },
        tooltip: {
          callbacks: {
            afterLabel: (context: any) => {
              const datasetIndex = context.datasetIndex;
              const stageIndex = context.dataIndex;

              if (datasetIndex < branchStages.length) {
                const branchStat = branchStages[datasetIndex];
                const stage = branchStat.stages[stageIndex];
                return [`${stage.percentage.toFixed(1)}% of branch articles`];
              } else {
                const percentage = chartData.percentages[stageIndex];
                const stage = data[stageIndex];
                const missing = stage.missingArticleIds.length;
                return [
                  t('widgets.articleProgress.percentageOfTotal', {
                    percentage: percentage.toFixed(1),
                  }),
                  missing > 0
                    ? t('widgets.articleProgress.articlesNotYetAtStage', {
                        count: missing,
                      })
                    : '',
                  missing > 0
                    ? t('widgets.articleProgress.clickToViewMissing')
                    : '',
                ];
              }
            },
          },
        },
      },
    }),
    [t, chartData.percentages, data, onOpen, branchStages],
  );

  return (
    <>
      <Box height="400px" width="100%">
        <Line data={chartData} options={options} />
      </Box>

      {selectedData && (
        <ArticlesOverviewModal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setSelectedData(null);
          }}
          filters={{
            searchTerm: '',
            sortBy: '',
            sortDesc: false,
            page: 1,
            ...defaultEmptyFilters,
            articleIds: selectedData.articleIds,
            branchIds: selectedData.branchId ? [selectedData.branchId] : [],
            showAllVariants: false,
          }}
          articleSearchProps={{
            filters: [
              FilterOptionType.Branches,
              FilterOptionType.ShowAllOptions,
            ],
            searchParams: {
              searchTerm: '',
              sortBy: '',
              sortDesc: false,
              page: 1,
              ...defaultEmptyFilters,
              articleIds: selectedData.articleIds,
              branchIds: selectedData.branchId ? [selectedData.branchId] : [],
              showAllVariants: false,
            },
          }}
          columns={() => TableColumns(t, true)}
        />
      )}
    </>
  );
}
